import React, { useCallback, useMemo, useState } from 'react';

import { addPerson, compose } from '@/assets/icons';
import { NewMessage } from '@/components/NewMessage';
import { EmptyResourceMessage } from '@/components/shared/EmptyResourceMessage';
import { InviteTeamMemberDialog } from '@/components/shared/InviteTeamMemberDialog';
import { SheetLayout } from '@/components/shared/SheetLayout';
import { libraryUrl } from '@/config/urls';
import { useListMyChatsQuery, useListTeamVideoChatsQuery } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { useFormattedTitle } from '@/hooks/useFormattedTitle';
import {
	useAnalyticsEvent,
	useFireAnalyticsPageView,
} from '@/providers/AnalyticsProvider';
import { useTeam } from '@/providers/TeamProvider';
import { useUnreadChats } from '@/providers/UnreadChatsProvider/UnreadChatsProvider';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router';

import { ChannelsCard } from './components/ChannelsCard';
import { DirectMessagesCard } from './components/DirectMessagesCard';
import styles from './DirectMessages.module.scss';

export const DirectMessages: React.FC = () => {
	const { team } = useTeam();
	const navigate = useNavigate();
	const { chatIds, videoChatIds } = useUnreadChats();

	const [creatingNewMessage, setCreatingNewMessage] =
		useState<boolean>(false);
	const [addTeamMemberOpen, setAddTeamMemberOpen] = useState<boolean>(false);
	const [tabIndex, setTabIndex] = useState(0);

	const [title] = useFormattedTitle('Asaph | Messages');
	const fireOpenInviteMembers = useAnalyticsEvent(
		'Messages_Open_Invite_Team',
	);
	const fireOpenSendNewMessage = useAnalyticsEvent(
		'Messages_Open_Send_New_Message',
	);
	const fireNavigateLibraryEvent = useAnalyticsEvent(
		'Channels_Navigate_Library',
	);

	useFireAnalyticsPageView({
		enabled: !!title,
		pageViewArgs: {
			title,
		},
	});

	const { data: chatsData, isLoading } = useListMyChatsQuery(
		{
			input: {
				limit: 1,
				offset: 0,
			},
		},
		{
			enabled: tabIndex === 0,
		},
	);

	const { data: videoChatsData, isLoading: isVideoChatsLoading } =
		useListTeamVideoChatsQuery(
			{
				input: {
					limit: -1,
					offset: 0,
				},
			},
			{
				enabled: tabIndex === 1,
			},
		);

	const openInviteDialog = useCallback(() => {
		fireOpenInviteMembers();
		setAddTeamMemberOpen(true);
	}, [fireOpenInviteMembers]);

	const openNewMessageDialog = useCallback(() => {
		fireOpenSendNewMessage();
		setCreatingNewMessage(true);
	}, [fireOpenSendNewMessage]);

	const chats = useMemo(() => chatsData?.listMyChats || [], [chatsData]);
	const noTeamMembers = (team.members?.length ?? 0) < 2;
	const noChats = !chats.length;

	const videoChats = useMemo(
		() => videoChatsData?.listTeamVideoChats || [],
		[videoChatsData],
	);

	return (
		<>
			<Helmet title={title} />
			<SheetLayout
				titleComponent="Messages"
				actions={[
					{
						icon: compose,
						onClick: openNewMessageDialog,
						hidden: noTeamMembers || tabIndex !== 0,
					},
					{
						icon: addPerson,
						onClick: openInviteDialog,
						hidden: !noTeamMembers,
					},
				]}
				isLoading={isLoading || (tabIndex === 1 && isVideoChatsLoading)}
			>
				{tabIndex === 0 ? (
					<div
						className={classNames([
							styles['direct-messages'],
							noChats && styles['full-height'],
						])}
					>
						{!noChats ? (
							chats.map((chat) => (
								<DirectMessagesCard
									key={`chat-${chat.id}`}
									chat={chat}
									isUnread={chatIds.includes(chat.id)}
								/>
							))
						) : (
							<EmptyResourceMessage
								onActionClick={() =>
									noTeamMembers
										? openInviteDialog()
										: setCreatingNewMessage(true)
								}
								title="There are no messages"
								subtitle="Send a new message to an individual or group"
								actionLabel={
									noTeamMembers
										? 'Invite Team Member'
										: 'New Message'
								}
							/>
						)}
					</div>
				) : (
					<div
						className={classNames([
							styles['direct-messages'],
							(noTeamMembers || videoChats.length === 0) &&
								styles['full-height'],
						])}
					>
						{noTeamMembers ? (
							<EmptyResourceMessage
								onActionClick={openInviteDialog}
								title="There are no team members"
								subtitle="Start messaging when a team member joins"
								actionLabel="Invite Team Member"
							/>
						) : videoChats.length ? (
							videoChats.map((chat) => (
								<ChannelsCard
									key={`video-chat-${chat.id}`}
									chat={chat}
									isUnread={videoChatIds.includes(chat.id)}
								/>
							))
						) : (
							<EmptyResourceMessage
								onActionClick={() => {
									navigate(libraryUrl);
									fireNavigateLibraryEvent();
								}}
								title="There are no conversation channels"
								subtitle="Start a conversation from within any video"
								actionLabel="View library"
							/>
						)}
					</div>
				)}
			</SheetLayout>

			<NewMessage
				open={creatingNewMessage}
				handleClose={() => setCreatingNewMessage(false)}
				initialMessage
			/>

			<InviteTeamMemberDialog
				open={addTeamMemberOpen}
				onClose={() => setAddTeamMemberOpen(false)}
			/>
		</>
	);
};
